import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';

import { whenTruthy } from 'ish-core/utils/operators';

import {
  DOT_CMS_CONTENT_TYPE_COMPONENT,
  DotCmsContentTypesComponentProvider,
  dotCmsContentTypeComponentProviders,
} from '../../../configurations/dot-cms-injection-keys';
import { DotCmsContentTypeComponent } from '../../../models/dot-cms-content-types-component.model';
import { CustomDotCmsAssignedContentlet } from '../../../models/page/custom-dot-cms-page.model';

@Component({
  selector: 'custom-dot-cms-content-render',
  templateUrl: './dot-cms-content-render.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [dotCmsContentTypeComponentProviders],
})
export class DotCmsContentRenderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() contentlet: CustomDotCmsAssignedContentlet;

  @ViewChild('dotCmsOutlet', { read: ViewContainerRef, static: true }) dotCmsOutlet: ViewContainerRef;

  private contentlet$ = new ReplaySubject<CustomDotCmsAssignedContentlet>(undefined);
  private destroy$ = new Subject<void>();
  constructor(private injector: Injector, private cdRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.contentlet$.pipe(whenTruthy(), takeUntil(this.destroy$)).subscribe(contentlet => {
      this.mapComponent(contentlet);
      this.cdRef.markForCheck();
    });
  }

  ngOnChanges(): void {
    this.contentlet$.next(this.contentlet);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  private mapComponent(contentlet: CustomDotCmsAssignedContentlet) {
    const components = this.injector.get<DotCmsContentTypesComponentProvider[]>(DOT_CMS_CONTENT_TYPE_COMPONENT, []);
    const mappedComponent =
      components.find(c => c.contentType === contentlet.contentType) ||
      components.find(c => c.contentType === 'fallbackNoComponent');
    if (mappedComponent) {
      const componentRef = this.createComponent(mappedComponent);
      this.initializeComponent(componentRef.instance, contentlet);
    } else {
      console.warn(`Component provider not available for the content: ${contentlet.contentType}`);
    }
  }

  private createComponent(mappedComponent: DotCmsContentTypesComponentProvider) {
    this.dotCmsOutlet.clear();
    return this.dotCmsOutlet.createComponent(mappedComponent.class);
  }

  private initializeComponent(instance: DotCmsContentTypeComponent, contentlet: CustomDotCmsAssignedContentlet) {
    instance.identifier = contentlet.identifier;
    instance.contentlet = contentlet;

    // OnChanges has to be manually invoked on dynamically created components
    if (instance.ngOnChanges) {
      instance.ngOnChanges({ identifier: new SimpleChange(undefined, contentlet.identifier, true) });
    }
  }
}
